import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import _ from 'lodash';

import { useCart } from 'hooks/use-cart';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { useLoyaltyContext } from 'state/loyalty';
import {
  compareBy_id,
  compareBy_idAndErrors,
  useConditionalState,
} from 'state/loyalty/hooks/use-conditional-state';
import { useLoyaltyOfferGroups } from 'state/loyalty/hooks/use-loyalty-offer-groups';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import { useMainMenuContext } from 'state/menu/main-menu';
import { IDayPartBoundary } from 'state/menu/main-menu/types';

export function useGetCmsAndLoyaltyOffers() {
  const [refreshOffersLoading, setRefreshOffersLoading] = useState(false);
  const {
    queryLoyaltyOffers,
    queryLoyaltyUserStandardOffers,
    loyaltyStandardOffersEnabled,
    refetchCmsOffers,
  } = useLoyaltyContext();
  const { loyaltyUserId, loading: loyaltyUserIdLoading } = useLoyaltyUserId();
  const { calculateCartTotal } = useCart();
  const {
    loading: offerGroupsLoading,
    refetch: refetchOfferGroups,
    offerGroups: cmsOfferGroups,
  } = useLoyaltyOfferGroups();
  const dispatch = useAppDispatch();
  const { activeDayParts } = useMainMenuContext();
  const dayPartsRef = useRef({
    setActiveDayPartOnLoad: false,
    prevDayParts: [] as IDayPartBoundary[],
  });

  const _cmsOffers = useAppSelector(selectors.loyalty.selectCmsOffers);
  const [cmsOffers, setCmsOffers] = useConditionalState(_cmsOffers, compareBy_id);
  const userOffers = useAppSelector(selectors.loyalty.selectUserOffers);
  const _personalizedOffers = useAppSelector(selectors.loyalty.selectPersonalizedOffers);
  const [personalizedOffers, setPersonalizedOffers] = useConditionalState(
    _personalizedOffers,
    compareBy_idAndErrors
  );
  const unauthenticatedOffers = useAppSelector(selectors.loyalty.selectOffers);

  useEffect(() => {
    setCmsOffers(_cmsOffers);
  }, [_cmsOffers, setCmsOffers]); // setCmsOffers is stable but ESLint doesn't know that

  useEffect(() => {
    setPersonalizedOffers(_personalizedOffers);
  }, [_personalizedOffers, setPersonalizedOffers]); // setPersonalizedOffers is stable but ESLint doesn't know that

  const baseOffers = !loyaltyUserIdLoading && loyaltyUserId ? userOffers : unauthenticatedOffers;

  // This refers to userOffers, personalizedOffers, cmsOffers and unauthenticatedOffers
  const offersLoading = useAppSelector(selectors.loyalty.selectOffersLoading);

  const cartTotal: number = useMemo(() => (calculateCartTotal ? calculateCartTotal() : 0), [
    calculateCartTotal,
  ]);

  const fetchOffers = useCallback(() => {
    // Reset shouldRefetchOffers flag
    dispatch(actions.loyalty.setShouldRefetchOffers(false));

    if (loyaltyStandardOffersEnabled) {
      if (loyaltyUserId) {
        queryLoyaltyUserStandardOffers({ loyaltyId: loyaltyUserId, subtotalAmount: cartTotal });
      } else {
        queryLoyaltyOffers({ subtotalAmount: cartTotal });
      }
    }
  }, [
    cartTotal,
    loyaltyUserId,
    loyaltyStandardOffersEnabled,
    queryLoyaltyOffers,
    queryLoyaltyUserStandardOffers,
    dispatch,
  ]);

  const refreshOffers = useCallback(async () => {
    // When a user manually refreshes we have to fetch the CMS offers
    // and the engine offers. These two offers have been implemented in different
    // ways and signal different loading mechanisms (one is through redux, the other a hook)
    // To standardize the way this component responds to loading, I've opted to
    // manually set the offers loading (which is currently what happens when you call fetchOffers)
    // I'm doing this eagerly so the refresh control doesn't close. FlatList will close the RefreshControl
    // if the refreshing state doesn't get signalled in an appropriate time.

    setRefreshOffersLoading(true);

    await Promise.all([refetchCmsOffers, refetchOfferGroups]).then(fetchOffers);

    setRefreshOffersLoading(false);
  }, [fetchOffers, refetchCmsOffers, refetchOfferGroups]);

  useEffect(() => {
    if (activeDayParts.length === 0) {
      return;
    }

    if (!_.isEqual(activeDayParts, dayPartsRef.current.prevDayParts)) {
      if (!dayPartsRef.current.setActiveDayPartOnLoad) {
        dayPartsRef.current.setActiveDayPartOnLoad = true;
      } else {
        refreshOffers();
      }

      dayPartsRef.current.prevDayParts = activeDayParts;
    }
  }, [activeDayParts, refreshOffers]);

  return {
    cmsOffers,
    cmsOfferGroups,
    personalizedOffers,
    baseOffers,
    offersLoading: offersLoading || offerGroupsLoading,
    refreshOffers,
    refreshOffersLoading,
  };
}
